import React, { useContext } from 'react';
import AuthContext from '../../auth/AuthContext';
import { Button } from '../../components/ui';
import './StorePage.css';

/**
 * StorePage — "Ye Olde Store"
 *
 * Mobile-first cosmetics store. Mirrors the MyTeamsPage layout conventions:
 *   - top banner with LEAGUES (left) and MYTHRIL balance (right)
 *   - navigation handled through the `setPage` prop (see AppRouter)
 *   - all controls use the shared <Button> component, re-skinned in StorePage.css
 *
 * Props:
 *   @param {Object}   user    - authenticated user (reserved for future per-user offers)
 *   @param {Function} setPage - navigation callback. Destinations used here:
 *                               'myTeams' (Leagues), 'storeSkins', 'storeBackgrounds',
 *                               'storeFoils', 'storeTaunts'
 */

// Store categories. `destination` strings are routed in AppRouter's setPage handler.
const STORE_CATEGORIES = [
    { id: 'skins', label: 'Skins', destination: 'storeSkins' },
    { id: 'backgrounds', label: 'Backgrounds', destination: 'storeBackgrounds' },
    { id: 'foils', label: 'Foils', destination: 'storeFoils' },
    { id: 'taunts', label: 'Taunts', destination: 'storeTaunts' }
];

function StorePage({ user, setPage }) {
    // Balance is owned by AuthContext (AuthProvider) — read from the global source
    // of truth instead of fetching independently, so it stays in sync with ShopPage
    // and MyTeamsPage without a separate network call.
    const { mythrilBalance } = useContext(AuthContext);

    const handleLeaguesClick = () => {
        console.log('StorePage: Navigating to MyTeams');
        if (setPage) {
            setPage('myTeams');
        }
    };

    const handleCategoryClick = (category) => {
        console.log('StorePage: Category clicked:', category.destination);
        if (setPage) {
            setPage(category.destination);
        }
    };

    return (
        <div className="store-page">
            {/* Header - conforming to header-component-implementation-guide.md */}
            <div className="page-header">
                <button className="header-back-button" onClick={handleLeaguesClick}>
                    <span className="back-arrow">◀</span>
                    <span className="back-label">LEAGUES</span>
                </button>

                <div className="header-right-group">
                    <button className="header-currency-button">
                        <img src="/art/diamond_400_white_bg.svg" alt="Diamond" className="diamond-icon" />
                        <span className="currency-label">{mythrilBalance} MYTHRIL</span>
                    </button>

                    <img
                        src="/art/UserIcon.png"
                        alt="User"
                        className="header-user-avatar"
                        onClick={() => {
                            if (!setPage) {
                                throw new Error('StorePage: setPage prop is required but not provided for account navigation');
                            }
                            setPage('accountPage');
                        }}
                        style={{ cursor: 'pointer' }}
                    />
                </div>
            </div>

            {/* Logo Banner */}
            <div className="store-hero">
                <img
                    src="/art/Store_Logo.png"
                    alt="Ye Olde Store"
                    className="store-logo"
                />
            </div>

            {/* Category Grid */}
            <div className="store-grid">
                {STORE_CATEGORIES.map((category) => (
                    <Button
                        key={category.id}
                        variant="secondary"
                        size="lg"
                        className="store-tile"
                        onClick={() => handleCategoryClick(category)}
                    >
                        <span className="store-tile-label">{category.label}</span>
                    </Button>
                ))}
            </div>
        </div>
    );
}

export default StorePage;
